<script setup>
import { ref, computed, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, requiredIf, minLength } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { useMasterProfile } from 'dashboard/composables/useAccountResource';
import ResetAgentPassword from './ResetAgentPassword.vue';
import Auth from '../../../../api/auth';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  email: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: '',
  },
  availability: {
    type: String,
    default: '',
  },
  provider: {
    type: String,
    default: '',
  },
  customRoleId: {
    type: Number,
    default: null,
  },
  isMaster: {
    type: Boolean,
    default: false,
  },
  isDisabled: { type: Boolean, default: false },
  canManageAccess: { type: Boolean, default: false },
  canResetPassword: { type: Boolean, default: false },
  canAssignMaster: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const { AVAILABILITY_STATUS_KEYS } = wootConstants;

const store = useStore();
const { t, te } = useI18n();
const canManageMaster = useMasterProfile();

const agentName = ref(props.name);
const agentAvailability = ref(props.availability);
const agentActive = ref(!props.isDisabled);
const submitting = ref(false);
const sendingResetLink = ref(false);
const isResettingPassword = ref(false);
const selectedRoleId = ref(
  props.isMaster ? 'master' : props.customRoleId || props.type
);
const masterPassword = ref('');
const masterRoleChange = computed(
  () => (selectedRoleId.value === 'master') !== props.isMaster
);

const rules = {
  agentName: { required, minLength: minLength(1) },
  selectedRoleId: { required },
  agentAvailability: { required },
  masterPassword: { required: requiredIf(masterRoleChange) },
};

const v$ = useVuelidate(rules, {
  agentName,
  selectedRoleId,
  agentAvailability,
  masterPassword,
});

const pageTitle = computed(
  () => `${t('AGENT_MGMT.EDIT.TITLE')} - ${props.name}`
);

const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  if (canManageMaster.value || props.canAssignMaster) {
    defaultRoles.unshift({
      id: 'master',
      name: 'master',
      label: t('ACCOUNT_RESOURCES.MASTER'),
    });
  }

  const customRoles = (props.isMaster ? [] : getCustomRoles.value).map(
    role => ({
      id: role.id,
      name: `custom_${role.id}`,
      label: role.name,
    })
  );

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const availabilityStatuses = computed(() =>
  statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    disabled: props.availability === AVAILABILITY_STATUS_KEYS[index],
  }))
);

const editAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid || submitting.value) return;
  submitting.value = true;

  try {
    const payload = {
      id: props.id,
      name: agentName.value,
      availability: agentAvailability.value,
    };

    if (masterRoleChange.value || props.isMaster) {
      payload.masterRoleUpdate = true;
      payload.role = selectedRole.value.name;
      if (masterRoleChange.value) {
        payload.master_password = masterPassword.value;
      }
    } else if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
      payload.custom_role_id = null;
    }

    if (props.canManageAccess && agentActive.value !== !props.isDisabled) {
      payload.disabled = !agentActive.value;
    }
    await store.dispatch('agents/update', payload);
    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    const accessErrorKey = `AGENT_LIFECYCLE.ERRORS.${error.response?.data?.code}`;
    const errorKey = `ACCOUNT_RESOURCES.MASTER_ROLE.ERRORS.${error.response?.data?.code}`;
    if (te(accessErrorKey)) {
      useAlert(t(accessErrorKey));
    } else if ((masterRoleChange.value || props.isMaster) && te(errorKey)) {
      useAlert(t(errorKey));
    } else if (
      (masterRoleChange.value || props.isMaster) &&
      [401, 403].includes(error.response?.status)
    ) {
      useAlert(t('ACCOUNT_RESOURCES.MASTER_ROLE.ERRORS.FORBIDDEN'));
    } else {
      useAlert(t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
    }
  } finally {
    masterPassword.value = '';
    submitting.value = false;
  }
};

watch(agentActive, () => {
  agentAvailability.value = 'offline';
});

watch(selectedRoleId, () => {
  if (selectedRoleId.value === 'master') agentActive.value = true;
  masterPassword.value = '';
  v$.value.masterPassword.$reset();
});

const sendResetLink = async () => {
  if (sendingResetLink.value || submitting.value) return;
  sendingResetLink.value = true;
  try {
    await Auth.resetPassword({ email: props.email });
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ERROR_MESSAGE'));
  } finally {
    sendingResetLink.value = false;
  }
};

const passwordResetDone = () => {
  isResettingPassword.value = false;
  store.dispatch('agents/get');
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <ResetAgentPassword
      v-if="isResettingPassword"
      :agent="{ id, name, email }"
      @close="isResettingPassword = false"
      @done="passwordResetDone"
    />
    <template v-else>
      <woot-modal-header :header-title="pageTitle" />
      <form class="w-full" @submit.prevent="editAgent">
        <fieldset
          :disabled="submitting || sendingResetLink"
          class="m-0 min-w-0 border-0 p-0"
        >
          <div class="w-full">
            <label :class="{ error: v$.agentName.$error }">
              {{ $t('AGENT_MGMT.EDIT.FORM.NAME.LABEL') }}
              <input
                v-model="agentName"
                type="text"
                :placeholder="$t('AGENT_MGMT.EDIT.FORM.NAME.PLACEHOLDER')"
                @input="v$.agentName.$touch"
              />
            </label>
          </div>

          <div class="w-full">
            <label :class="{ error: v$.selectedRoleId.$error }">
              {{ $t('ACCOUNT_RESOURCES.MASTER_ROLE.PROFILE_LABEL') }}
              <select
                v-model="selectedRoleId"
                @change="v$.selectedRoleId.$touch"
              >
                <option v-for="role in roles" :key="role.id" :value="role.id">
                  {{ role.label }}
                </option>
              </select>
              <span v-if="v$.selectedRoleId.$error" class="message">
                {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.ERROR') }}
              </span>
            </label>
          </div>

          <div v-if="masterRoleChange" class="w-full">
            <label :class="{ error: v$.masterPassword.$error }">
              {{ $t('ACCOUNT_RESOURCES.MASTER_ROLE.PASSWORD_LABEL') }}
              <input
                v-model="masterPassword"
                type="password"
                autocomplete="new-password"
                :placeholder="
                  $t('ACCOUNT_RESOURCES.MASTER_ROLE.PASSWORD_PLACEHOLDER')
                "
                @input="v$.masterPassword.$touch"
              />
              <span v-if="v$.masterPassword.$error" class="message">
                {{ $t('ACCOUNT_RESOURCES.MASTER_ROLE.PASSWORD_REQUIRED') }}
              </span>
            </label>
            <p class="mt-1 text-xs text-n-slate-11">
              {{ $t('ACCOUNT_RESOURCES.MASTER_ROLE.REFRESH_HINT') }}
            </p>
          </div>

          <div
            class="grid grid-cols-1 gap-x-4"
            :class="{ 'sm:grid-cols-2': canManageAccess }"
          >
            <div class="min-w-0">
              <label :class="{ error: v$.agentAvailability.$error }">
                {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.LABEL') }}
                <select
                  v-model="agentAvailability"
                  :disabled="!agentActive"
                  @change="v$.agentAvailability.$touch"
                >
                  <option
                    v-for="status in availabilityStatuses"
                    :key="status.value"
                    :value="status.value"
                  >
                    {{ status.label }}
                  </option>
                </select>
                <span v-if="v$.agentAvailability.$error" class="message">
                  {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_AVAILABILITY.ERROR') }}
                </span>
              </label>
            </div>
            <div
              v-if="canManageAccess"
              class="mb-4 min-w-0"
              role="group"
              :aria-label="t('AGENT_LIFECYCLE.ACCESS_LABEL')"
            >
              <label :for="`agent-access-${id}`">
                {{ t('AGENT_LIFECYCLE.ACCESS_LABEL') }}
              </label>
              <div
                class="field-base mb-0 flex h-10 items-center justify-between gap-3"
              >
                <span
                  class="flex min-w-0 items-center gap-2 text-sm font-normal"
                >
                  <span
                    aria-hidden="true"
                    class="size-1.5 shrink-0 rounded-full"
                    :class="agentActive ? 'bg-n-brand' : 'bg-n-slate-7'"
                  />
                  {{
                    t(
                      agentActive
                        ? 'AGENT_LIFECYCLE.STATUS_ACTIVE'
                        : 'AGENT_LIFECYCLE.STATUS_DISABLED'
                    )
                  }}
                </span>
                <Switch
                  :id="`agent-access-${id}`"
                  v-model="agentActive"
                  :aria-label="t('AGENT_LIFECYCLE.ACTIVE_LABEL')"
                  :aria-describedby="
                    !agentActive ? `agent-access-hint-${id}` : undefined
                  "
                  :disabled="submitting || selectedRoleId === 'master'"
                />
              </div>
              <p
                v-if="!agentActive"
                :id="`agent-access-hint-${id}`"
                class="mb-0 mt-1 text-xs leading-5 text-n-slate-11"
              >
                {{ t('AGENT_LIFECYCLE.INACTIVE_HINT') }}
              </p>
              <p
                v-else-if="selectedRoleId === 'master'"
                class="mb-0 mt-1 text-xs leading-5 text-n-slate-11"
              >
                {{ t('AGENT_LIFECYCLE.MASTER_ACTIVE_HINT') }}
              </p>
            </div>
          </div>

          <section
            v-if="provider !== 'saml'"
            class="mb-3"
            :aria-label="t('AGENT_PASSWORD.PASSWORD')"
          >
            <h3 class="!mb-2 !mt-0 text-sm font-medium text-n-slate-12">
              {{ t('AGENT_PASSWORD.PASSWORD') }}
            </h3>
            <div class="flex flex-col items-start gap-1 sm:flex-row sm:gap-2">
              <Button
                ghost
                slate
                size="sm"
                type="button"
                icon="i-lucide-mail"
                :label="t('AGENT_PASSWORD.SEND_RESET_LINK')"
                :is-loading="sendingResetLink"
                :disabled="sendingResetLink"
                @click.prevent="sendResetLink"
              />
              <Button
                v-if="canResetPassword"
                ghost
                slate
                size="sm"
                type="button"
                icon="i-lucide-key-round"
                :label="t('AGENT_PASSWORD.SET_NEW_PASSWORD')"
                :disabled="sendingResetLink"
                @click="isResettingPassword = true"
              />
            </div>
          </section>
          <div class="flex w-full justify-end py-2">
            <div
              class="grid grid-cols-1 items-center gap-2 min-[24rem]:grid-cols-2 sm:flex sm:justify-end"
            >
              <Button
                faded
                slate
                type="reset"
                :label="$t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
                @click.prevent="emit('close')"
              />
              <Button
                type="submit"
                :label="t('AGENT_LIFECYCLE.SAVE_CHANGES')"
                :disabled="v$.$invalid || submitting"
                :is-loading="submitting"
              />
            </div>
          </div>
        </fieldset>
      </form>
    </template>
  </div>
</template>
